/**
 * Regras da Parte Diária — lançamento de MVE (mvt_mve) e de EPP
 * (mvt_equipamento_pequeno), espelho do SIGA web
 * (ESTUDO-LANCAMENTO-MVE-EPP-APP.md: 3.3.4, 3.4.2, 3.5, 3.6/Anexo B, 4.5, 4.7).
 *
 * ARQUIVO DUPLICADO DE PROPÓSITO — existe idêntico em:
 *   backend/src/parteDiaria/regras.ts                 (validação/cálculo no sync)
 *   web/src/features/parteDiaria/parteDiariaRegras.ts (prévia + travas da tela)
 * O teste backend/test/parteDiariaRegras.test.ts falha se os dois divergirem.
 * Sem imports: precisa compilar nos dois projetos (por isso hojeSP/dataBR/arred
 * são redeclarados aqui em vez de importados do carreteiro).
 *
 * Escopo do app (decisão 2026-09-15): só INSERÇÃO; MVE só no dia de hoje;
 * EPP só período do mês corrente terminando até hoje.
 */

export type SN = 'S' | 'N'
export type Perfil = 'AS' | 'AC' | 'CE' | 'SU' | 'AD' | 'UC'
/** Tipo da MVE na lista do módulo (A.1/A.2): M = contrato MVE, M+C = MVE + carreteiro, E = EPP. */
export type TipoParte = 'M' | 'M+C' | 'E'
export type ModuloParte = 'MVE' | 'EPP'
/** cdt_forma_remuneracao.forma_remuneracao_formulario_medicao: MVE = horímetro/odômetro inteiro; P = produção decimal. */
export type FormularioMedicao = 'MVE' | 'P'
export type MveTp = 'M' | 'V' | 'E'
export type PapelLinha = 'PRINCIPAL' | 'FRACAO'
/** DIA_NOVO = hoje sem linha no SIGA (1ª linha é a principal). FRACOES = hoje já tem linha; tudo é fração. */
export type ModoDia = 'DIA_NOVO' | 'FRACOES'

export const moduloDoTipo = (t: TipoParte): ModuloParte => (t === 'E' ? 'EPP' : 'MVE')

// ============================================================================
//  Utilitários
// ============================================================================

export const vazio = (v: unknown): boolean =>
  v === null || v === undefined || String(v).trim() === ''
export const dec = (v: unknown): number =>
  vazio(v) ? 0 : Number(String(v).replace(',', '.')) || 0
export const int = (v: unknown): number => (vazio(v) ? 0 : parseInt(String(v), 10) || 0)
export const arred2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100
export const arred4 = (n: number): number => Math.round((n + Number.EPSILON) * 10000) / 10000

/** Data de hoje (ou de `quando`) no fuso de São Paulo, formato YYYY-MM-DD. */
export function hojeSP(quando: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(quando)
}

/** 'YYYY-MM-DD' → 'DD/MM/AAAA' (formato das mensagens do SIGA). */
export function dataBR(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

/** Soma dias a uma data 'YYYY-MM-DD' (negativo subtrai). */
export function somarDias(iso: string, dias: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + dias * 86_400_000)
  return dt.toISOString().slice(0, 10)
}

/** 'YYYY-MM-DD' → 'YYYY-MM'. */
export const mesDe = (iso: string): string => iso.slice(0, 7)
export const primeiroDiaDoMes = (iso: string): string => `${mesDe(iso)}-01`
/** Número de dias do mês (n das fórmulas). */
export function diasNoMes(ano: number, mes: number): number {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate()
}
export function ultimoDiaDoMes(iso: string): string {
  const [y, m] = iso.slice(0, 10).split('-').map(Number)
  return `${mesDe(iso)}-${String(diasNoMes(y ?? 1970, m ?? 1)).padStart(2, '0')}`
}
/** Dias inclusivos entre duas datas ISO (QR do EPP). */
export function diasInclusivos(dtInicial: string, dtFinal: string): number {
  const [y1, m1, d1] = dtInicial.slice(0, 10).split('-').map(Number)
  const [y2, m2, d2] = dtFinal.slice(0, 10).split('-').map(Number)
  const a = Date.UTC(y1 ?? 1970, (m1 ?? 1) - 1, d1 ?? 1)
  const b = Date.UTC(y2 ?? 1970, (m2 ?? 1) - 1, d2 ?? 1)
  return Math.floor((b - a) / 86_400_000) + 1
}
export const MESES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
] as const
export function nomeDoMes(iso: string): string {
  return MESES_PT[Number(iso.slice(5, 7)) - 1] ?? iso.slice(0, 7)
}

export const ehAdmin = (p: Perfil | string | null | undefined): boolean => p === 'AS' || p === 'AC'
export const ehChefeOuSuporte = (p: Perfil | string | null | undefined): boolean => p === 'CE' || p === 'SU'

/** Troca acentuadas por ASCII, como classFerramenta::removerAcento (observação). */
export function removerAcento(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/** HH:MM estrito (§3.5 #6 — o SIGA aceita "7" por bug; aqui não). */
export function horaValida(h: unknown): boolean {
  return typeof h === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(h)
}
export function minutosDeHora(h: string): number {
  const [hh, mm] = h.split(':')
  return Number(hh) * 60 + Number(mm)
}

/** Chapa vazia grava '000000' (§3.3.2 #9). */
export function normalizarChapa(chapa: string | null | undefined): string {
  const s = (chapa ?? '').trim()
  return s === '' ? '000000' : s
}

/** Prazo retroativo (§1.9): data < referência − N dias. `dias` null = regra desligada. */
export function foraDoPrazoRetroativo(dt: string, referencia: string, dias: number | null): boolean {
  if (dias == null) return false
  return dt.slice(0, 10) < somarDias(referencia, -dias)
}

/** Datas que o app oferece para lançar, da mais antiga para a mais nova: ontem e hoje. */
export function datasPermitidas(hoje: string): string[] {
  return [somarDias(hoje, -1), hoje]
}

/**
 * `dt` do lançamento: o dia em que foi capturado ou o dia ANTERIOR (decisão
 * 2026-09-21: o app lança ontem e hoje), nunca no futuro. A referência é a
 * captura, não o envio — lançamento feito offline continua válido dias depois.
 */
export function dataDoLancamentoValida(dt: string, capturedAtIso: string, hojeServidor: string): boolean {
  const capturada = new Date(capturedAtIso)
  if (Number.isNaN(capturada.getTime())) return false
  const d = dt.slice(0, 10)
  return datasPermitidas(hojeSP(capturada)).includes(d) && d <= hojeServidor
}

// ============================================================================
//  MVE — configurações, serviço, linha
// ============================================================================

/** sys_configuracao (A.14). null = chave inativa/ausente = regra desligada. */
export interface ConfigParteDiaria {
  difKm: number | null
  difHora: number | null
  horaInicioFim: number | null
  diasRetroativo: number | null
}

/** cdt_grupo: 3 = improdutividade, 4 = manutencao-e-mecanica (exigem diferença zero). */
export const GRUPOS_DIFERENCA_ZERO: readonly number[] = [3, 4]

export interface ServicoParte {
  id: number
  nome: string
  /** 'S' = parada mecânica, não paga */
  isento: boolean
  grupoId: number | null
  grupoNome?: string | null
  inativo: boolean
}

export const servicoExigeDiferencaZero = (s: ServicoParte | null | undefined): boolean =>
  s?.grupoId != null && GRUPOS_DIFERENCA_ZERO.includes(s.grupoId)

/** Campos de uma linha de mvt_mve que as regras leem. */
export interface LinhaMveRegra {
  obraId: number | null
  servicoExecutadoId: number | null
  equipeId: number | null
  medicaoInicio: number | null
  medicaoTermino: number | null
  chapa: string | null
  hInicio: string | null
  hFim: string | null
}

export type CampoLinha =
  | 'obra'
  | 'servico'
  | 'equipe'
  | 'medicaoInicio'
  | 'medicaoTermino'
  | 'hInicio'
  | 'hFim'
  | 'geral'

export interface ErroLinha {
  campo: CampoLinha
  code: string
  mensagem: string
}

export interface CtxLinhaMve {
  papel: PapelLinha
  /** Dia do mês (1..31) — vai nas mensagens "no dia X". */
  dia: number
  formularioMedicao: FormularioMedicao
  mveTp: MveTp
  /** cdt_projeto.projeto_cmve da obra da linha: CMVE não tem consistência de km/h e horas. */
  projetoCmve: boolean
  configs: ConfigParteDiaria
  servico: ServicoParte | null
  /** cdt_mve_transferencia.ultimo_hodometro de transferência ativa NA data (§3.5 #12). */
  hodometroTransferenciaNoDia: number | null
  /** idem no dia SEGUINTE (§3.5 #13). */
  hodometroTransferenciaDiaSeguinte: number | null
}

/**
 * Validações de campo da inserção (§3.5, itens 1–13; sem #9b, que é só admin).
 * Devolve todos os erros, na ordem em que o SIGA os testa.
 */
export function validarLinhaMve(l: LinhaMveRegra, ctx: CtxLinhaMve): ErroLinha[] {
  const erros: ErroLinha[] = []
  const X = ctx.dia
  const fracao = ctx.papel === 'FRACAO'
  const inicio = l.medicaoInicio ?? 0
  const temTermino = l.medicaoTermino != null
  const termino = l.medicaoTermino ?? 0

  // 1. Tudo ou nada: obra, serviço, horas (e equipe na principal; término na fração)
  const faltando: CampoLinha[] = []
  if (l.obraId == null) faltando.push('obra')
  if (l.servicoExecutadoId == null) faltando.push('servico')
  if (vazio(l.hInicio)) faltando.push('hInicio')
  if (vazio(l.hFim)) faltando.push('hFim')
  if (!fracao && l.equipeId == null) faltando.push('equipe')
  if (fracao && !temTermino) faltando.push('medicaoTermino')
  if (faltando.length > 0) {
    const msg = fracao ? `Preencha todos os dados do dia fracionado ${X}` : `Preencha todos os dados do dia ${X}`
    for (const campo of faltando) erros.push({ campo, code: 'CAMPOS_OBRIGATORIOS', mensagem: msg })
  }

  // 2. Início > término
  if (temTermino && inicio > termino) {
    erros.push({
      campo: 'medicaoTermino',
      code: 'HORIMETRO_INVALIDO',
      mensagem: `Hodrometro / horimetro inicial não pode ser maior ou igual ao final no dia ${X}`,
    })
  }
  // 3/4. Diferença × grupo do serviço
  if (temTermino && ctx.servico) {
    const exigeZero = servicoExigeDiferencaZero(ctx.servico)
    if (inicio < termino && exigeZero) {
      erros.push({
        campo: 'medicaoTermino',
        code: 'HORIMETRO_INVALIDO',
        mensagem: `A coluna diferença do serviço selecionado não pode ser maior que zero, no dia ${X}`,
      })
    }
    if (inicio === termino && !exigeZero) {
      erros.push({
        campo: 'medicaoTermino',
        code: 'HORIMETRO_INVALIDO',
        mensagem: `O serviço selecionado não permite diferença igual a zero, no dia ${X}`,
      })
    }
  }
  // 5. Serviço inativo
  if (ctx.servico?.inativo) {
    erros.push({ campo: 'servico', code: 'SERVICO_INATIVO', mensagem: `Serviço inativo, selecione outro serviço no dia ${X}` })
  }
  // 6. Horas válidas
  const hIniOk = horaValida(l.hInicio)
  const hFimOk = horaValida(l.hFim)
  if (!vazio(l.hInicio) && !hIniOk) {
    erros.push({ campo: 'hInicio', code: 'HORA_INVALIDA', mensagem: `Hora inicial inválida no dia ${X}` })
  }
  if (!vazio(l.hFim) && !hFimOk) {
    erros.push({ campo: 'hFim', code: 'HORA_INVALIDA', mensagem: `Hora final inválida no dia ${X}` })
  }
  if (hIniOk && hFimOk) {
    const mi = minutosDeHora(l.hInicio as string)
    const mf = minutosDeHora(l.hFim as string)
    // 7. Início >= fim (não vira a meia-noite)
    if (mi >= mf) {
      erros.push({
        campo: 'hFim',
        code: 'HORA_INVALIDA',
        mensagem: `A hora inicio não pode ser maior ou igual a hora final no dia ${X}`,
      })
    } else if (!fracao && !ctx.projetoCmve && ctx.configs.horaInicioFim != null) {
      // 8. Diferença de horas > config (só projeto não CMVE; frações não passam por aqui no SIGA)
      if ((mf - mi) / 60 > ctx.configs.horaInicioFim) {
        erros.push({
          campo: 'hFim',
          code: 'HORA_INVALIDA',
          mensagem: `A diferença da hora não pode ultrapassar ${ctx.configs.horaInicioFim} h no dia ${X}`,
        })
      }
    }
  }
  // 9. Diferença km/h > config (não CMVE, formulário MVE, só principal)
  if (temTermino && !fracao && !ctx.projetoCmve && ctx.formularioMedicao === 'MVE') {
    const dif = termino - inicio
    if (ctx.mveTp === 'V' && ctx.configs.difKm != null && dif > ctx.configs.difKm) {
      erros.push({
        campo: 'medicaoTermino',
        code: 'HORIMETRO_INVALIDO',
        mensagem: `A diferença do km não pode ultrapassar ${ctx.configs.difKm} km no dia ${X}`,
      })
    }
    if (ctx.mveTp === 'M' && ctx.configs.difHora != null && dif > ctx.configs.difHora) {
      erros.push({
        campo: 'medicaoTermino',
        code: 'HORIMETRO_INVALIDO',
        mensagem: `A diferença de hora não pode ultrapassar ${ctx.configs.difHora} h no dia ${X}`,
      })
    }
  }
  // 11. Início > 0 e término = 0
  if (inicio > 0 && termino === 0) {
    erros.push({
      campo: 'medicaoTermino',
      code: 'HORIMETRO_INVALIDO',
      mensagem: `O horímetro/km final é obrigatório no dia ${X}.`,
    })
  }
  // 12. Transferência ativa no dia: início ≥ hodômetro registrado (só principal)
  if (!fracao && ctx.hodometroTransferenciaNoDia != null && inicio < ctx.hodometroTransferenciaNoDia) {
    erros.push({
      campo: 'medicaoInicio',
      code: 'HORIMETRO_INVALIDO',
      mensagem: `O horímetro/km inicial informado (${inicio}) não pode ser inferior ao registrado na transferência (${ctx.hodometroTransferenciaNoDia}).`,
    })
  }
  // 13. Transferência ativa no dia seguinte: término ≥ hodômetro registrado
  if (temTermino && ctx.hodometroTransferenciaDiaSeguinte != null && termino < ctx.hodometroTransferenciaDiaSeguinte) {
    erros.push({
      campo: 'medicaoTermino',
      code: 'HORIMETRO_INVALIDO',
      mensagem: `O horímetro/km final informado (${termino}) não pode ser inferior ao registrado na transferência (${ctx.hodometroTransferenciaDiaSeguinte}).`,
    })
  }
  return erros
}

/**
 * Horímetro inicial sugerido (§3.3.4): max(último término ≤ dia, hodômetro da
 * última transferência ativa ≤ dia); 0 → primeiro horímetro do cadastro.
 * Inteiro (o SIGA trunca). Formulário P não tem início.
 */
export function calcularHorimetroSugerido(b: {
  ultimoTermino: number | null
  hodometroTransferencia: number | null
  primeiroHorimetro: number | null
  formularioMedicao: FormularioMedicao
}): number | null {
  if (b.formularioMedicao === 'P') return null
  const v = Math.trunc(Math.max(b.ultimoTermino ?? 0, b.hodometroTransferencia ?? 0))
  return v > 0 ? v : Math.trunc(b.primeiroHorimetro ?? 0)
}

/** Início de cada linha do POST: a 1ª recebe `inicio0`; as demais, o término da anterior (§3.4 #7). */
export function encadearInicios(inicio0: number | null, terminos: Array<number | null>): Array<number | null> {
  const out: Array<number | null> = []
  let atual = inicio0
  for (const t of terminos) {
    out.push(atual)
    atual = t ?? atual
  }
  return out
}

/** mve_transferencia_id / _insercao_id na inserção (§3.4.2). */
export const TRANSFERENCIA_ADMIN = 2
export function transferenciaIdParaInsercao(admin: boolean, ultimaId: number | null): number | null {
  return admin ? TRANSFERENCIA_ADMIN : ultimaId
}

/** mve_km_real (§3.4.3): só MVE com envia_km_rabbot e histórico; senão 0. */
export function calcularKmReal(
  anterior: number | null,
  inicio: number,
  termino: number,
  enviaKmRabbot: boolean,
): number {
  if (!enviaKmRabbot || anterior == null) return 0
  return anterior + (termino - inicio)
}

// ============================================================================
//  MVE — modo do dia (principal × frações)
// ============================================================================

export interface LinhaBloqueio {
  data: string
  transferenciaId: number | null
  medicaoGerada: SN | null
  chefeEquipeId: number | null
}

export function classificarModoDia(i: {
  modo: ModoDia
  dt: string
  linhasHoje: LinhaBloqueio[]
  chefeSessaoId: number | null
  admin: boolean
  /** Último dia lançado pelo chefe da sessão na MVE (qualquer mês). */
  ultimoDtChefe: string | null
}): { ok: true } | { ok: false; code: string; message: string } {
  if (i.modo === 'DIA_NOVO' && i.linhasHoje.length > 0) {
    return {
      ok: false,
      code: 'DIA_JA_LANCADO',
      message: `O dia ${dataBR(i.dt)} já possui lançamento no SIGA. Recarregue o contexto para lançar frações.`,
    }
  }
  if (i.modo === 'FRACOES' && i.linhasHoje.length === 0) {
    return {
      ok: false,
      code: 'DIA_SEM_PRINCIPAL',
      message: `O dia ${dataBR(i.dt)} ainda não possui lançamento principal no SIGA. Recarregue o contexto.`,
    }
  }
  if (i.modo === 'FRACOES' && !i.admin) {
    // §3.3.2 #16: CE/SU só fraciona o último dia lançado por ele, em linha dele
    const principal = i.linhasHoje[0]
    const linhaDele = principal != null && principal.chefeEquipeId === i.chefeSessaoId
    if (!linhaDele || i.ultimoDtChefe !== i.dt.slice(0, 10)) {
      return {
        ok: false,
        code: 'FRACAO_NAO_PERMITIDA',
        message: 'Só é possível fracionar o último dia lançado pelo próprio chefe de equipe.',
      }
    }
  }
  return { ok: true }
}

// ============================================================================
//  MVE — bloqueio da linha/dia (mvtMveSvcBloquearMovimentacao, Anexo B)
// ============================================================================

export interface SessaoBloqueio {
  perfil: Perfil
  usuarioId: number
  /** chefe_equipe_id da sessão — só SU (chefe ao qual o suporte está vinculado). */
  chefeEquipeId: number | null
}

export interface ContextoBloqueioMve {
  mveId: number
  /** Destino da transferência que gerou a linha da lista (A.1) — "campo oculto" do web. */
  mveChefeEquipeId: number | null
  ultimaTransferenciaId: number | null
  transferenciaDtUltima: string | null
  /** Destino da última transferência (A.5). Normalmente = mveChefeEquipeId. */
  destinoId: number | null
  origemId: number | null
  origemDt: string | null
  origemAnteriorId: number | null
  origemAnteriorDt: string | null
  /** MIN/MAX dos itens de contrato do tipo no mês (A.3). */
  contratoDtInicio: string | null
  contratoDtFinal: string | null
}

export type MotivoBloqueio = 'TRANSFERENCIA' | 'MEDICAO_GERADA' | 'FORA_CONTRATO' | 'OUTRO_CHEFE'

export interface ResultadoBloqueio {
  bloqueado: boolean
  /** Passo do Anexo B que decidiu (1..11). */
  passo: number
  motivo: MotivoBloqueio | null
}

export function mensagemBloqueio(motivo: MotivoBloqueio | null, dt: string): string {
  switch (motivo) {
    case 'MEDICAO_GERADA':
      return `A medição do dia ${dataBR(dt)} já foi gerada.`
    case 'FORA_CONTRATO':
      return `O dia ${dataBR(dt)} está fora do período de vigência do contrato.`
    case 'TRANSFERENCIA':
      return `Você não possui permissão para inserir movimentação no dia ${dataBR(dt)} (transferência da MVE).`
    case 'OUTRO_CHEFE':
      return `Você não possui permissão para inserir movimentação no dia ${dataBR(dt)} (MVE de outro chefe de equipe).`
    default:
      return `Você não possui permissão para inserir movimentação no dia ${dataBR(dt)}.`
  }
}

/**
 * Port fiel de mvtMveSvcBloquearMovimentacao (Anexo B): 11 passos, na ordem,
 * o primeiro que casa decide. Diferenças documentadas:
 *  - passo 1 ("receptor antes da transferência") compara com o destino da
 *    ÚLTIMA transferência (A.5), não com o destino da lista: para CE/SU o
 *    destino da lista é sempre o próprio chefe (é o filtro de A.1), e com ele
 *    o remetente nunca poderia lançar dias em branco antes da transferência —
 *    o que contradiz o passo 3 e os cenários do fim do Anexo B;
 *  - passo 6: precedência corrigida — sem datas de contrato não bloqueia
 *    (decisão §7.17; no web `dia > fim` bloqueava mesmo sem contrato);
 *  - datas chegam validadas (ISO); comparação lexicográfica; `null` = hoje
 *    (compararData do SIGA), só relevante nos passos 3, 6 e 8.
 * `qtdOutroChefeNoDia` = COUNT de linhas de outro chefe na data (A.8), lida antes.
 */
export function bloquearMovimentacaoMve(
  s: SessaoBloqueio,
  ctx: ContextoBloqueioMve,
  l: LinhaBloqueio,
  qtdOutroChefeNoDia: number,
  hoje: string,
): ResultadoBloqueio {
  const r = (bloqueado: boolean, passo: number, motivo: MotivoBloqueio | null): ResultadoBloqueio => ({
    bloqueado,
    passo,
    motivo,
  })
  const lt = (a: string, b: string | null) => a < (b ?? hoje)
  const gt = (a: string, b: string | null) => a > (b ?? hoje)
  const data = l.data.slice(0, 10)
  const ceOuSu = ehChefeOuSuporte(s.perfil)
  const admin = ehAdmin(s.perfil)
  const chefeAtualId = s.perfil === 'CE' ? s.usuarioId : s.chefeEquipeId
  const isOwnRow = l.chefeEquipeId != null && l.chefeEquipeId === chefeAtualId
  const sessaoEhDestinoLista = ceOuSu && chefeAtualId != null && chefeAtualId === ctx.mveChefeEquipeId
  const sessaoEhDestinoUltima = ceOuSu && chefeAtualId != null && chefeAtualId === ctx.destinoId
  const sessaoNaoEhDestinoUltima = ceOuSu && chefeAtualId !== ctx.destinoId
  const sessaoEh = (id: number | null) => ceOuSu && id != null && chefeAtualId === id
  const tDt = ctx.transferenciaDtUltima
  /** dia < transferência, ou dia = transferência e já existe linha de outro chefe. */
  const antesOuNoDiaComOutro = (t: string) => lt(data, t) || (data === t && qtdOutroChefeNoDia > 0)

  // 1. Receptor antes da transferência (ou no dia, se o remetente já lançou), linha não é dele
  if (sessaoEhDestinoUltima && tDt && antesOuNoDiaComOutro(tDt) && !isOwnRow) return r(true, 1, 'TRANSFERENCIA')

  // 2. Linha gerada bloqueia todos (inclusive admin)
  if (l.medicaoGerada === 'S') return r(true, 2, 'MEDICAO_GERADA')

  // 3. Não-destino liberado: sem origem | linha dele | remetente direto no seu período | dono anterior no intervalo
  if (
    sessaoNaoEhDestinoUltima &&
    (ctx.origemId == null ||
      isOwnRow ||
      (sessaoEh(ctx.origemId) && (ctx.origemDt == null || !lt(data, ctx.origemDt))) ||
      (ctx.origemAnteriorId != null &&
        sessaoEh(ctx.origemAnteriorId) &&
        (ctx.origemAnteriorDt == null || !lt(data, ctx.origemAnteriorDt)) &&
        lt(data, ctx.origemDt))) &&
    tDt != null &&
    antesOuNoDiaComOutro(tDt) &&
    ((l.chefeEquipeId == null && l.transferenciaId == null) || isOwnRow)
  ) {
    return r(false, 3, null)
  }

  // 4. Admin com linha existente não gerada
  if (admin && l.medicaoGerada === 'N') return r(false, 4, null)

  // 5. Destino da lista com linha da última transferência
  if (sessaoEhDestinoLista && l.transferenciaId != null && l.transferenciaId === ctx.ultimaTransferenciaId) {
    return r(false, 5, null)
  }

  // 6. Fora da vigência do contrato do mês (vale para admin)
  if (
    (ctx.contratoDtInicio != null && ctx.contratoDtFinal != null && lt(data, ctx.contratoDtInicio)) ||
    (ctx.contratoDtFinal != null && gt(data, ctx.contratoDtFinal))
  ) {
    return r(true, 6, 'FORA_CONTRATO')
  }

  // 7. Não-destino depois da transferência
  if (sessaoNaoEhDestinoUltima && tDt != null && gt(data, tDt)) return r(true, 7, 'TRANSFERENCIA')

  // 8. CE/SU antes da transferência e não é o destino (testa usuario_id E chefe_equipe_id)
  if (ceOuSu && lt(data, tDt) && s.usuarioId !== ctx.destinoId && s.chefeEquipeId !== ctx.destinoId) {
    return r(true, 8, 'TRANSFERENCIA')
  }

  // 9. Linha de outra transferência
  if (l.transferenciaId != null && l.transferenciaId !== ctx.ultimaTransferenciaId) {
    if (sessaoEhDestinoLista && isOwnRow) return r(false, 9, null) // retransferência
    if (l.transferenciaId === TRANSFERENCIA_ADMIN && admin) return r(false, 9, null)
    return r(true, 9, 'OUTRO_CHEFE')
  }

  // 10. CE/SU que não é o destino da lista
  if (s.perfil === 'CE' && s.usuarioId !== ctx.mveChefeEquipeId) return r(true, 10, 'OUTRO_CHEFE')
  if (s.perfil === 'SU' && s.chefeEquipeId !== ctx.mveChefeEquipeId) return r(true, 10, 'OUTRO_CHEFE')

  // 11.
  return r(false, 11, null)
}

// ============================================================================
//  EPP — período e valor
// ============================================================================

export interface PeriodoEpp {
  dtInicial: string
  dtFinal: string
}

/** Sobreposição inclusiva (§4.5 inserção #4) + o caso "engloba", que o web não cobre. */
export function periodoSobrepoe(nova: PeriodoEpp, existente: PeriodoEpp): boolean {
  const ni = nova.dtInicial.slice(0, 10)
  const nf = nova.dtFinal.slice(0, 10)
  const ei = existente.dtInicial.slice(0, 10)
  const ef = existente.dtFinal.slice(0, 10)
  return (ei <= ni && ni <= ef) || (ei <= nf && nf <= ef) || (ni <= ei && nf >= ef)
}

export type CampoEpp = 'obra' | 'servico' | 'dtInicial' | 'dtFinal' | 'geral'

export interface ErroEpp {
  campo: CampoEpp
  code: string
  mensagem: string
}

export interface CtxPeriodoEpp {
  /** "Hoje" de referência (data da captura); define o mês e o limite da data final. */
  hojeRef: string
  existentes: PeriodoEpp[]
  admin: boolean
  transferenciaDtUltima: string | null
  /** MAX(dt_final) de outro chefe no mês (A.15). */
  ultimaDtFinalOutroChefe: string | null
  servico: ServicoParte | null
}

/** §4.5 (inserção) + obrigatoriedades que o web não tem (§7.7) + "até hoje" (decisão do app). */
export function validarPeriodoEpp(
  n: { obraId: number | null; servicoExecutadoId: number | null; dtInicial: string | null; dtFinal: string | null },
  ctx: CtxPeriodoEpp,
): ErroEpp[] {
  const erros: ErroEpp[] = []
  if (n.obraId == null) erros.push({ campo: 'obra', code: 'CAMPOS_OBRIGATORIOS', mensagem: 'Selecione a obra' })
  if (n.servicoExecutadoId == null) {
    erros.push({ campo: 'servico', code: 'CAMPOS_OBRIGATORIOS', mensagem: 'Selecione o serviço executado' })
  }
  if (vazio(n.dtInicial)) erros.push({ campo: 'dtInicial', code: 'CAMPOS_OBRIGATORIOS', mensagem: 'Informe a data inicial' })
  if (vazio(n.dtFinal)) erros.push({ campo: 'dtFinal', code: 'CAMPOS_OBRIGATORIOS', mensagem: 'Informe a data final' })
  if (ctx.servico?.inativo) {
    erros.push({ campo: 'servico', code: 'SERVICO_INATIVO', mensagem: 'Serviço inativo, selecione outro serviço.' })
  }
  if (vazio(n.dtInicial) || vazio(n.dtFinal)) return erros
  const ini = (n.dtInicial as string).slice(0, 10)
  const fim = (n.dtFinal as string).slice(0, 10)
  const hoje = ctx.hojeRef.slice(0, 10)

  // 1. Inicial > final
  if (ini > fim) {
    erros.push({ campo: 'dtFinal', code: 'PERIODO_INVALIDO', mensagem: 'A data inicial não pode ser superior a data final' })
    return erros
  }
  // 2. Dentro do mês corrente
  if (mesDe(ini) !== mesDe(hoje) || mesDe(fim) !== mesDe(hoje)) {
    erros.push({
      campo: 'dtInicial',
      code: 'PERIODO_FORA_DO_MES',
      mensagem: `O periodo de lançamento precisa ser no mês de ${nomeDoMes(hoje)}`,
    })
  }
  // Só até hoje (o app não lança futuro)
  if (fim > hoje) {
    erros.push({ campo: 'dtFinal', code: 'DATA_FUTURA', mensagem: 'A data final não pode ser posterior a hoje' })
  }
  // 4. Sobreposição com linhas do mês
  if (ctx.existentes.some((e) => periodoSobrepoe({ dtInicial: ini, dtFinal: fim }, e))) {
    erros.push({
      campo: 'dtInicial',
      code: 'PERIODO_SOBREPOSTO',
      mensagem: 'A data inicial não pode ser menor ou igual a movimentação anterior',
    })
  }
  // 7. CE/SU: a partir da transferência e depois do último período de outro chefe
  if (!ctx.admin) {
    if (ctx.transferenciaDtUltima != null && ini < ctx.transferenciaDtUltima.slice(0, 10)) {
      erros.push({
        campo: 'dtInicial',
        code: 'ANTES_DA_TRANSFERENCIA',
        mensagem: `Não é possível inserir antes da data da transferência (${dataBR(ctx.transferenciaDtUltima)}).`,
      })
    }
    if (ctx.ultimaDtFinalOutroChefe != null && ini <= ctx.ultimaDtFinalOutroChefe.slice(0, 10)) {
      const apos = somarDias(ctx.ultimaDtFinalOutroChefe, 1)
      erros.push({
        campo: 'dtInicial',
        code: 'ANTES_DA_TRANSFERENCIA',
        mensagem: `O remetente já possui lançamento até ${dataBR(ctx.ultimaDtFinalOutroChefe)}. Insira a partir de ${dataBR(apos)}.`,
      })
    }
  }
  return erros
}

export interface LinhaEppCalculo {
  id: number | string
  dtInicial: string
  dtFinal: string
  isento: boolean
  /** contrato_item_preco_unitario_produtivo do item da própria linha (§7.6). */
  pup: number
}

export interface ResultadoEppLinha {
  id: number | string
  /** dias inclusivos */
  qr: number
  /** equipamento_valor */
  vr: number
  /** equipamento_pequeno_qr */
  qrGravar: number
}

/**
 * §4.7 / Anexo E.3 (calculoEPP + mvtEquipamentoPequenoSvcCalcular), com Pup por linha.
 * `n` = dias do mês. Todas as linhas do mês entram, inclusive isentas.
 */
export function calcularEppMes(
  linhas: LinhaEppCalculo[],
  n: number,
): { linhas: ResultadoEppLinha[]; valorRealizadoMes: number } {
  const qrs = linhas.map((l) => diasInclusivos(l.dtInicial, l.dtFinal))
  let eQR = 0
  let eCaPM = 0
  let eQRCa = 0
  linhas.forEach((l, i) => {
    const qr = qrs[i] ?? 0
    eQR += qr
    if (l.isento) eCaPM += qr
    else eQRCa += qr
  })
  const vr = (l: LinhaEppCalculo, qr: number): number => {
    if (l.isento) return 0
    if (eQR - eCaPM === 1) return l.pup / 30
    if (eQRCa === n) return (qr * l.pup) / n
    if (eCaPM > 0 && eQR / n === 1) return qr * ((l.pup - (eCaPM * l.pup) / 30) / (eQR - eCaPM))
    return (qr * l.pup) / 30
  }
  const vrs = linhas.map((l, i) => vr(l, qrs[i] ?? 0))
  const total = vrs.reduce((a, b) => a + b, 0)
  const out: ResultadoEppLinha[] = linhas.map((l, i) => {
    const qr = qrs[i] ?? 0
    let qrGravar = 0
    if (!l.isento && eQRCa > 0 && l.pup > 0) {
      const qrLinha = ((total / l.pup) * 30) / eQRCa
      qrGravar = arred4(qrLinha * qr)
    }
    return { id: l.id, qr, vr: arred4(vrs[i] ?? 0), qrGravar }
  })
  return { linhas: out, valorRealizadoMes: arred4(total) }
}

// ============================================================================
//  Sync — recusas que não adianta corrigir no app
// ============================================================================

/** 4xx de negócio que não voltam para edição (a ficha só pode ser descartada). */
export const RECUSAS_SEM_CORRECAO_PD: readonly string[] = [
  'DATA_INVALIDA',
  'DATA_FUTURA',
  'PERFIL_NAO_SUPORTADO',
  'SEM_PERMISSAO_METODO',
  'MVE_FORA_DA_LISTA',
  'MVE_TIPO_INCOMPATIVEL',
  'SEM_CONTRATO_NO_MES',
  'SEM_CONTRATO_NA_DATA',
  'DIA_BLOQUEADO',
  'FRACAO_NAO_PERMITIDA',
  'PRAZO_RETROATIVO',
  'MEDICAO_GERADA',
  'ANTES_DA_TRANSFERENCIA',
  'OBRA_NAO_ENCONTRADA',
  'PROJETO_NAO_AUTORIZADO',
]
